import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  setToken, removeToken, hasToken,
  loginApi, registerApi, getMeApi,
  updateProfileApi, changePasswordApi,
  uploadProfilePhotoApi, removeProfilePhotoApi,
  logoutApi,
} from '../services/authApi';

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the app to provide authentication state.
 *
 * Exposes:
 *   - user          : current user object or null
 *   - isAuthenticated: boolean
 *   - isLoading     : true while validating stored token on mount
 *   - login(email, password)   : returns { success, message? }
 *   - register(name, email, password) : returns { success, message? }
 *   - logout()
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * On mount, check if we have a stored token and validate it
   */
  useEffect(() => {
    let cancelled = false;

    async function validateToken() {
      if (!hasToken()) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await getMeApi();
        if (!cancelled) {
          if (data.success && data.user) {
            setUser(data.user);
          } else {
            removeToken();
            setUser(null);
          }
        }
      } catch {
        if (!cancelled) {
          removeToken();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    validateToken();
    return () => { cancelled = true; };
  }, []);

  /**
   * Listen for 401 events from authFetch (token expired / invalid)
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      removeToken();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  /**
   * Login with email & password
   */
  const login = useCallback(async (email, password) => {
    try {
      const data = await loginApi(email, password);
      if (data.success && data.token) {
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (err) {
      return { success: false, message: 'Network error — is the server running?' };
    }
  }, []);

  /**
   * Register a new account (no organization needed)
   */
  const register = useCallback(async (name, email, password) => {
    try {
      const data = await registerApi(name, email, password);
      if (data.success && data.token) {
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      }
      return { success: false, message: data.message || 'Registration failed' };
    } catch (err) {
      return { success: false, message: 'Network error — is the server running?' };
    }
  }, []);

  /**
   * Update user profile (name, email)
   */
  const updateProfile = useCallback(async (data) => {
    try {
      const result = await updateProfileApi(data);
      if (result.success && result.user) {
        setUser(result.user);
        return { success: true };
      }
      return { success: false, message: result.message || 'Update failed' };
    } catch (err) {
      return { success: false, message: 'Network error' };
    }
  }, []);

  /**
   * Change password
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const result = await changePasswordApi(currentPassword, newPassword);
      if (result.success) {
        if (result.token) setToken(result.token);
        return { success: true };
      }
      return { success: false, message: result.message || 'Password change failed' };
    } catch (err) {
      return { success: false, message: 'Network error' };
    }
  }, []);

  /**
   * Upload profile photo
   */
  const uploadProfilePhoto = useCallback(async (file) => {
    try {
      const result = await uploadProfilePhotoApi(file);
      if (result.success && result.user) {
        setUser(result.user);
        return { success: true };
      }
      return { success: false, message: result.message || 'Upload failed' };
    } catch (err) {
      return { success: false, message: 'Network error' };
    }
  }, []);

  /**
   * Remove profile photo
   */
  const removeProfilePhoto = useCallback(async () => {
    try {
      const result = await removeProfilePhotoApi();
      if (result.success && result.user) {
        setUser(result.user);
        return { success: true };
      }
      return { success: false, message: result.message || 'Remove failed' };
    } catch (err) {
      return { success: false, message: 'Network error' };
    }
  }, []);

  /**
   * Logout — clear token and user
   */
  const logout = useCallback(() => {
    logoutApi(); // Clear httpOnly cookie on server (fire-and-forget)
    removeToken();
    setUser(null);
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    uploadProfilePhoto,
    removeProfilePhoto,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to consume auth context
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthContext;
